import React, { useEffect, useState } from "react";
import { collection, getDocs, doc, getDoc, setDoc } from "firebase/firestore";
import { txtdb, auth } from "../../firebase-config";
import { signOut } from "firebase/auth";
import { NavLink, useNavigate } from "react-router-dom";

// Icons
import logo from "../../stock/nexus/logo.png";
import { IoIosLogOut } from "react-icons/io";
import { LuCopy, LuCheck } from "react-icons/lu"; 
import { AiOutlineMenu } from "react-icons/ai";
import { PiHandSwipeLeft, PiUserCircleDuotone } from "react-icons/pi";
import { IoCloseOutline, IoWalletOutline } from "react-icons/io5";
import { FiEdit3, FiX } from "react-icons/fi";

function Users() {
  const navigate = useNavigate();
  
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [copied, setCopied] = useState(false);
  const [copiedEmail, setCopiedEmail] = useState(null);
  const [open, setOpen] = useState(false);

  // Balance Management States
  const [userBalances, setUserBalances] = useState({ USDT: 0, Bitcoin: 0, Ethereum: 0, Solana: 0 });
  const [editBalances, setEditBalances] = useState({ USDT: 0, Bitcoin: 0, Ethereum: 0, Solana: 0 });
  const [isEditingBalance, setIsEditingBalance] = useState(false);
  const [loadingBalances, setLoadingBalances] = useState(false);
  const [saveStatus, setSaveStatus] = useState(null); // 'saving', 'success', 'error'

  // Derived state to track if there are any unsaved changes
  const hasUnsavedChanges = ["USDT", "Bitcoin", "Ethereum", "Solana"].some(
    (asset) => Number(userBalances[asset]) !== Number(editBalances[asset])
  );

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const usersRef = collection(txtdb, "kycInfo");
        const snapshot = await getDocs(usersRef);
        const userList = [];

        snapshot.forEach((docSnap) => {
          userList.push({
            id: docSnap.id,
            ...docSnap.data(),
          });
        });

        setUsers(userList);
      } catch (error) {
        console.error("Error fetching users:", error);
      }
    };

    fetchUsers();
  }, []);

const fetchUserBalances = async (userId) => {
    setLoadingBalances(true);
    setIsEditingBalance(false);
    setSaveStatus(null);
    
    const assets = ["USDT", "Bitcoin", "Ethereum", "Solana"];
    let fetched = { USDT: 0, Bitcoin: 0, Ethereum: 0, Solana: 0 };
    
    try {
      // Create an array of fetch promises so they run in parallel
      const fetchPromises = assets.map(async (asset) => {
        const balanceRef = doc(txtdb, "users", userId, "balances", asset);
        const snap = await getDoc(balanceRef);
        return { 
          asset, 
          balance: snap.exists() ? snap.data().balance || 0 : 0 
        };
      });

      // Wait for all 4 requests to finish simultaneously
      const results = await Promise.all(fetchPromises);
      
      // Map the results back to our object
      results.forEach(({ asset, balance }) => {
        fetched[asset] = balance;
      });

      setUserBalances(fetched);
      setEditBalances(fetched);
    } catch (error) {
      console.error("Error fetching balances:", error);
      // Optional: If you want to show an error state to the user, you can add a state here
    } finally {
      setLoadingBalances(false); // This guarantees the skeleton loader stops even if a request fails
    }
  };

  const handleSaveBalances = async () => {
    if (!selectedUser) return false;
    setSaveStatus('saving');
    
    const assets = ["USDT", "Bitcoin", "Ethereum", "Solana"];
    try {
      for (const asset of assets) {
        const balanceRef = doc(txtdb, "users", selectedUser.id, "balances", asset);
        await setDoc(balanceRef, { balance: Number(editBalances[asset]) }, { merge: true });
      }
      // Update original state so hasUnsavedChanges becomes false
      setUserBalances({...editBalances}); 
      setSaveStatus('success');
      setIsEditingBalance(false);
      setTimeout(() => setSaveStatus(null), 2500);
      return true;
    } catch (error) {
      console.error("Error saving balances:", error);
      setSaveStatus('error');
      return false;
    }
  };

  const handleSaveAndExit = async () => {
    const success = await handleSaveBalances();
    if (success) {
      closePopup();
    }
  };

  const handleCancelChanges = () => {
    setEditBalances(userBalances);
    setIsEditingBalance(false);
    setSaveStatus(null);
  };

  const openUserDetails = (user) => {
    setSelectedUser(user);
    fetchUserBalances(user.id);
  };

  const closePopup = () => {
    setSelectedUser(null);
    setIsEditingBalance(false);
    setSaveStatus(null);
  };

  const handleCopy = async () => {
    if (selectedUser?.email) {
      await navigator.clipboard.writeText(selectedUser.email);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    }
  };

  const handleCopyEmail = (email) => {
    navigator.clipboard.writeText(email);
    setCopiedEmail(email);
    setTimeout(() => setCopiedEmail(null), 1500);
  };

  const handleLogout = () => {
    signOut(auth)
      .then(() => navigate("/login"))
      .catch((error) => console.error("Logout error:", error));
  };

  return (
    <div className="layout admin">
      <div className="content">

        {/* --- Top Navigation --- */}
        <div className="transactions-container container">
          <div className="navigation-container">
            <NavLink className="mobile">
              <img src={logo} alt="nexusforgefx" />
              <p className='name'>All Users</p>
            </NavLink>

            <p className='desktop'>All Users</p>

            <div className="actions desktop">
              <div className="quick" onClick={() => setOpen(!open)}>
                Admin Pages <AiOutlineMenu />
              </div>
              <div className="notif" onClick={handleLogout}>
                <IoIosLogOut className="icon" title="logout" />
              </div>
            </div>

            <div className="actions mobile">
              <div className="notif" onClick={handleLogout}>
                <IoIosLogOut className="icon" title="logout" />
              </div>
              <div className="nav" onClick={() => setOpen(!open)}>
                <AiOutlineMenu className='icon' />
              </div>
            </div>
          </div>

          {open && (
            <div className="admin-nav">
              <NavLink to="/pendingDeposits">Pending Deposits</NavLink>
              <NavLink to="/all-users">All Users</NavLink>
              <NavLink to="/copytrades">Active Copy Trades</NavLink>
              <NavLink to="/adminKycReview">KYC Reviews</NavLink>
              <NavLink to="/admin-wallets">Wallet Management</NavLink>
            </div>
          )}
        </div>

        {/* --- Main Content --- */}
        <div className="pages-content" onClick={() => setOpen(false)}>
          <div className="users-page">
            
            <div className="header-bar">
              <h1><PiUserCircleDuotone /> Registered Users</h1>
              <p>Manage members and update asset balances.</p>
            </div>

            {users.length > 0 && (
              <p className='mobile swipe-hint'><PiHandSwipeLeft /> Swipe table to see more</p>
            )}

            <div className="transaction-history">
              {users.length === 0 ? (
                <div className="nothing-yet">
                  <h4>No Users Found</h4>
                  <div className="info">User data will appear here once registered.</div>
                </div>
              ) : (
                <div className="table-wrapper">
                  <table className="user-table">
                    <thead>
                      <tr>
                        <th>Full Name</th>
                        <th>Email Address</th>
                        <th>Country</th>
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {users.map((user) => (
                        <tr key={user.id}>
                          <td className="name-cell">{user.firstName} {user.lastName}</td>
                          <td className="email-cell">
                            <span>{user.email}</span>
                            <button
                              className="copy-btn"
                              onClick={() => handleCopyEmail(user.email)}
                              title="Copy email"
                            >
                              {copiedEmail === user.email ? (
                                <LuCheck className="copy-icon success" />
                              ) : (
                                <LuCopy className="copy-icon" />
                              )}
                            </button>
                          </td>
                          <td className="country-cell">{user.country || "N/A"}</td>
                          <td>
                            <button
                              className="details-btn"
                              onClick={() => openUserDetails(user)}
                            >
                              Manage User
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

          </div>

          {/* --- User Details Modal --- */}
          {selectedUser && (
            <div className="user-popup-overlay" onClick={closePopup}>
              <div className="popup-content" onClick={(e) => e.stopPropagation()}>
                
                <div className="popup-header">
                  <h3>User Profile & Assets</h3>
                  <button className="close-icon-btn" onClick={closePopup}>
                    <IoCloseOutline />
                  </button>
                </div>

                <div className="modal-scroll-area">
                  {/* Personal Info Section */}
                  <div className="profile-section">
                    <div className="avatar-placeholder">
                      {selectedUser.firstName.charAt(0)}{selectedUser.lastName.charAt(0)}
                    </div>
                    <div className="name-block">
                      <h2>{selectedUser.firstName} {selectedUser.lastName}</h2>
                      <div className="email-block">
                        <p>{selectedUser.email}</p>
                        {copied ? (
                          <LuCheck className="copy-icon success" />
                        ) : (
                          <LuCopy className="copy-icon" onClick={handleCopy} title="Copy Email"/>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* KYC / Data Grid */}
                  <div className="data-grid">
                    <div className="data-item">
                      <span className="label">Phone</span>
                      <span className="value">{selectedUser.phone || "N/A"}</span>
                    </div>
                    <div className="data-item">
                      <span className="label">Country</span>
                      <span className="value">{selectedUser.country || "N/A"}</span>
                    </div>
                    <div className="data-item">
                      <span className="label">Gender</span>
                      <span className="value">{selectedUser.gender || "N/A"}</span>
                    </div>
                    <div className="data-item">
                      <span className="label">DOB</span>
                      <span className="value">{selectedUser.dob || "N/A"}</span>
                    </div>
                  </div>

                  {/* Wallet & Balance Management Section */}
                  <div className="balance-management-section">
                    <div className="balance-header">
                      <div className="title">
                        <IoWalletOutline className="icon"/>
                        <h4>Asset Balances</h4>
                      </div>
                      
                      {!loadingBalances && (
                        <button 
                          className={`edit-toggle-btn ${isEditingBalance ? 'cancel' : ''}`}
                          onClick={() => {
                            if (isEditingBalance && hasUnsavedChanges) {
                               // Optional: Do nothing if they click edit while unsaved, force them to use footer
                            } else {
                               setIsEditingBalance(!isEditingBalance);
                            }
                          }}
                        >
                          {isEditingBalance ? <><FiX /> Cancel</> : <><FiEdit3 /> Edit Balances</>}
                        </button>
                      )}
                    </div>

                    <div className="balance-body">
                      {loadingBalances ? (
                        <div className="assets-grid">
                          {[1, 2, 3, 4].map((i) => (
                            <div className="asset-card skeleton-card" key={i}>
                              <div className="skeleton skeleton-text short"></div>
                              <div className="skeleton skeleton-text long"></div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="assets-grid">
                          {["USDT", "Bitcoin", "Ethereum", "Solana"].map(asset => (
                            <div className="asset-card" key={asset}>
                              <span className="asset-name">{asset}</span>
                              {isEditingBalance ? (
                                <div className="input-wrapper">
                                  <span className="currency-symbol">$</span>
                                  <input 
                                    type="number" 
                                    step="any"
                                    value={editBalances[asset]} 
                                    onChange={(e) => setEditBalances({...editBalances, [asset]: e.target.value})}
                                  />
                                </div>
                              ) : (
                                <span className="asset-value">
                                  ${Number(userBalances[asset]).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 6 })}
                                </span>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="popup-footer">
                  <span className="created-date">
                    Joined: {selectedUser.createdAt ? new Date(selectedUser.createdAt).toLocaleDateString() : "Unknown"}
                  </span>
                  
                  {/* Dynamic Morphing Action Buttons */}
                  <div className="footer-actions">
                    {saveStatus === 'error' && <span className="error-msg">Failed to save.</span>}
                    {saveStatus === 'success' && <span className="success-msg">Saved!</span>}

                    {hasUnsavedChanges ? (
                      <>
                        <button className="cancel-btn" onClick={handleCancelChanges}>Cancel</button>
                        <button 
                          className="save-exit-btn" 
                          onClick={handleSaveAndExit}
                          disabled={saveStatus === 'saving'}
                        >
                          {saveStatus === 'saving' ? 'Saving...' : 'Save & Exit'}
                        </button>
                      </>
                    ) : (
                      <button className="close-btn" onClick={closePopup}>Done</button>
                    )}
                  </div>
                </div>

              </div>
            </div>
          )}

        </div>
      </div>
    </div>
  );
}

export default Users;